import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { compose } from "recompose";
import { push } from 'connected-react-router';
import { connect } from "react-redux";
import { getI18n } from "../../utils/intl";
import { hasAdminRole } from '../../utils';
import { getUserDetails } from '../../reducers/portalConfig';
import PropTypes from "prop-types";
import styles from "./styles";
import clsx from "clsx";
import {
  Loading,
  AlertMessages,
  FormActionButtons,
  Switch,
  FormTextField,
  Footer,
} from "../../components";
import {
  withStyles,
  Typography,
  Checkbox,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@material-ui/core";
import {
  fetchRegistrationSettings,
  updateRegistrationSettings,
} from "../../actions/registrationSettings";
import {
  getIsLoading,
  getIsFormLoadingError,
  getRegistrationSettings,
  getUpdateErrorMsg,
  getIsFormUpdateError,
  getIsUpdateSuccessful,
} from "../../reducers/registrationSettings";
import {
  ALERT_ERROR,
  ALERT_SUCCESS,
  REGISTRATION_SETTINGS_STATUS_ENABLED,
  REGISTRATION_SETTINGS_STATUS_DISABLED,
  REGISTRATION_SETTINGS_TERMS_OF_USE_DEFAULT,
  REGISTRATION_SETTINGS_LIMIT_DISABLED,
  REGISTRATION_SETTINGS_LIMIT_ENABLED,
  REGISTRATION_SETTINGS_LIMIT_ID_DEFAULT,
  REGISTRATION_SETTINGS_TERMS_OF_USE_MAX_LENGTH,
} from "../../constants";

export const RegistrationSettings = (props) => {
  const {
    isFormLoadingError,
    isUpdateError,
    updateErrorMsg,
    classes,
    registrationSettings,
    isLoading,
    isUpdateSuccessful,
    updateRegistrationSettings,
    user,
  } = props;

  const [registrationSettingsState, setRegistrationSettingsState] = useState(registrationSettings);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationStatus, setNotificationStatus] = useState("");

  const intl = getI18n(useIntl());
  const notifyMessages = (status, message) => {
    setNotificationStatus(status);
    setNotificationMessage(message);
  };

  useEffect(() => {
    if(!hasAdminRole(user)) {
      props.push('/404');
    }
    props.fetchRegistrationSettings();
  }, []);

  useEffect(() => {
    if (registrationSettings) {
      setRegistrationSettingsState(registrationSettings);
    }
  }, [registrationSettings]);

  useEffect(() => {
    if (isFormLoadingError) {
      notifyMessages(ALERT_ERROR, intl.getI18nMessage("error.settings.registrationsettings.load"));
    }
  }, [isFormLoadingError]);

  useEffect(() => {
    if(isUpdateSuccessful) {
      setRegistrationSettingsState(registrationSettings);
      setIsFormDirty(false);
      notifyMessages(ALERT_SUCCESS, intl.getI18nMessage('label.settings.registrationsettings.update.success'))
      setTimeout(() => { notifyMessages('', '')}, 2500);
    }
  }, [isUpdateSuccessful]);

  useEffect(() => {
    if (isUpdateError) {
      notifyMessages(ALERT_ERROR,updateErrorMsg || intl.getI18nMessage("error.settings.registrationsettings.update"));
      setTimeout(() => { notifyMessages('', '')}, 2500);
    }
  }, [isUpdateError]);

  const handleUnload = (evt) => {
    const unsaved_changes_warning = intl.getI18nMessage("label.user.profile.unsaved.changes.description");
    evt.returnValue = unsaved_changes_warning;
    return unsaved_changes_warning;
  };
  useEffect(() => {
    if (isFormDirty) {
      window.addEventListener("beforeunload", handleUnload);
      return () => {
        window.removeEventListener("beforeunload", handleUnload);
      };
    }
  }, [isFormDirty]);

  const onSave = () => {
      updateRegistrationSettings(registrationSettingsState);
  };

  const onCancel = () => {
    setIsFormDirty(false);
    setRegistrationSettingsState(registrationSettings);
  };

  const onRegistrationSwitchChange = (checked) => {
    const dirty = checked ?
        registrationSettings["registrationStatus"] === REGISTRATION_SETTINGS_STATUS_DISABLED
      :
        registrationSettings["registrationStatus"] === REGISTRATION_SETTINGS_STATUS_ENABLED;
    setIsFormDirty(dirty);
    setRegistrationSettingsState( {
      ...registrationSettingsState,
      "registrationStatus" : checked ? REGISTRATION_SETTINGS_STATUS_ENABLED : REGISTRATION_SETTINGS_STATUS_DISABLED,
    });
  }

  const onClickLimitUserRegistration = (event) => {
    const checked = event.target.checked;
    const dirty = checked ?
      registrationSettings["userRegistrationLimitEnabled"] === REGISTRATION_SETTINGS_LIMIT_DISABLED
      :
      registrationSettings["userRegistrationLimitEnabled"] === REGISTRATION_SETTINGS_LIMIT_ENABLED;
    setIsFormDirty(dirty);
    setRegistrationSettingsState( {
      ...registrationSettingsState,
      "userRegistrationLimitEnabled" : checked ? REGISTRATION_SETTINGS_LIMIT_ENABLED : REGISTRATION_SETTINGS_LIMIT_DISABLED,
    });
  }
  const onTermsOfUseChange = (value) => {
    const dirty = value !== registrationSettings.termOfUse;
    setIsFormDirty(dirty);
    setRegistrationSettingsState( {
      ...registrationSettingsState,
      "termOfUse" : value,
    });
  }
  const parseNewLine = (value) => value.replace(/(\\n)/g, "\n");

  const renderRegistrationSettings = () =>
    <div>
      <div className={classes.workflowHead}>
        <Typography variant="h4" className={ classes.smallTitle }>
          {intl.getI18nMessage(`label.settings.registrationsettings.registration.title`)}
        </Typography>
        <Switch
          id={"registration-settings-registration-switch"}
          checked={registrationSettingsState["registrationStatus"] === REGISTRATION_SETTINGS_STATUS_ENABLED}
          onChange={onRegistrationSwitchChange}
        />
      </div>
      <hr className={classes.hr}/>
      <Typography className={classes.bodyText}>
        {intl.getI18nMessage(`label.settings.registrationsettings.registration.title.helpertext`)}
      </Typography>
      {registrationSettingsState["registrationStatus"] === REGISTRATION_SETTINGS_STATUS_ENABLED && renderRegistrationWorkflow()}
    </div>;

  const renderRegistrationWorkflow = () =>
    <>
      {/*Limit User Registration Checkbox */}
      <ListItem
        className={classes.listItem}
      >
        <ListItemIcon className={classes.listItemIcon}>
          <Checkbox
            id="registration-settings-limit-user-checkbox"
            size="small"
            edge="start"
            disableRipple
            inputProps={{
              'aria-label': 'primary checkbox',
            }}
            onClick={onClickLimitUserRegistration}
            checked={registrationSettingsState.userRegistrationLimitEnabled}
          />
        </ListItemIcon>
        <ListItemText
          className={classes.listItemText}
          primary={intl.getI18nMessage("label.settings.registrationsettings.limituser.title")}
          primaryTypographyProps={{ className : classes.listItemTextPrimary }}
          secondary={intl.getI18nMessage("label.settings.registrationsettings.limituser.subtitle")}
          secondaryTypographyProps={{ className : classes.listItemTextSecondary }}
        />
      </ListItem>

      {/*Terms of Use Text Field*/}
      <div className={classes.textFieldContainer}>
        <FormTextField
          id={'registration-settings-termsofuse-textfield'}
          name={intl.getI18nMessage('label.settings.registrationsettings.registration.termsOfUse')}
          value={parseNewLine(registrationSettingsState.termOfUse)}
          handleChange={onTermsOfUseChange}
          helperText={
            intl.getI18nMessage('label.settings.registrationsettings.registration.termsOfUse.helpertext')
            + ` Maximum length is ${REGISTRATION_SETTINGS_TERMS_OF_USE_MAX_LENGTH} characters.`
          }
          defaultValue={parseNewLine(registrationSettingsState.termOfUse)}
          multiline={true}
          rows={25}
          maxLength={REGISTRATION_SETTINGS_TERMS_OF_USE_MAX_LENGTH}
        />
      </div>
    </>;

  return (
    <div>
      {isLoading ? (
        <Loading />
      ) : (
        <div className={classes.registrationSettingsContainer}>
          <div className={classes.details}>
            <Typography variant="h6" className={classes.smallLabel}>
              {intl.getI18nMessage("label.settings.title")}
            </Typography>
            <Typography variant="h1" className={classes.pageTitle}>
              {intl.getI18nMessage("label.settings.registrationsettings.title")}
            </Typography>
            {renderRegistrationSettings()}
          </div>
          { isFormDirty && <FormActionButtons
            isContainerFooter={true}
            id={"registration-settings-footer-buttons"}
            cancelText={intl.getI18nMessage("label.cancel.button")}
            nextText={intl.getI18nMessage("label.save.button")}
            onCancelClick={onCancel}
            onNextClick={onSave}
            isDisabled={!isFormDirty}
          />}
        </div>
      )}
      {notificationMessage!=='' && (
        <AlertMessages
        id={'registration-settings-alert-messages'}
        variant={notificationStatus}
        message={notificationMessage}
        containerClass={clsx(
          classes.notificationContainerRegistrationSettings,
          classes.notificationWidthClass,
        )}
        onClose={() => notifyMessages('', '')}
        hasNotificationIcon={true}
        />
      )}
      <Footer />
    </div>
  );
};
const mapStateToProps = (state) => ({
  registrationSettings: getRegistrationSettings(state),
  isLoading: getIsLoading(state),
  isUpdateError: getIsFormUpdateError(state),
  isUpdateSuccessful: getIsUpdateSuccessful(state),
  isFormLoadingError: getIsFormLoadingError(state),
  updateErrorMsg: getUpdateErrorMsg(state),
  user: getUserDetails(state),
});

const mapDispatchToProps = {
  fetchRegistrationSettings,
  updateRegistrationSettings,
  push,
};

RegistrationSettings.propTypes = {
  registrationSettings: PropTypes.object,
  updateRegistrationSettings: PropTypes.func,
  isLoading: PropTypes.bool,
  isFormLoadingError: PropTypes.bool,
  isUpdateError: PropTypes.bool,
  updateErrorMsg: PropTypes.string,
  isUpdateSuccessful: PropTypes.bool,
  fetchRegistrationSettings: PropTypes.func,
  user: PropTypes.object,
  push: PropTypes.func,
  classes: PropTypes.object,
};

RegistrationSettings.defaultProps = {
  registrationSettings : {
    termOfUse: REGISTRATION_SETTINGS_TERMS_OF_USE_DEFAULT,
    registrationStatus: REGISTRATION_SETTINGS_STATUS_DISABLED,
    userRegistrationLimitEnabled: REGISTRATION_SETTINGS_LIMIT_DISABLED,
    userRegistrationLimitId: REGISTRATION_SETTINGS_LIMIT_ID_DEFAULT,
  },
}

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps))(RegistrationSettings);
